import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import TextField from '@mulesoft/anypoint-components/lib/TextField';
import Select from '@mulesoft/anypoint-components/lib/Select';
import styles from '../AuthenticationFields.css';

const ADD_MODE = 'add';
const EDIT_MODE = 'edit';

/**
 * GoDaddy ANS SSO Key Authentication Form
 *
 * Renders authentication fields for GoDaddy ANS using SSO Key:
 * - API Key (gdKey)
 * - API Secret (gdSecret - sensitive, cleared in edit mode)
 * - Environment (production or ote)
 */
const SsoKeyForm = ({ mode = ADD_MODE, formData, onChange }) => {
  const environmentOptions = [
    { label: 'Production', value: 'production' },
    { label: 'OTE', value: 'ote' }
  ];

  const handleGdKeyChange = useCallback(
    (e) => onChange('gdKey', e.value),
    [onChange]
  );

  const handleGdSecretChange = useCallback(
    (e) => onChange('gdSecret', e.value),
    [onChange]
  );

  const handleEnvironmentChange = useCallback(
    (event) => onChange('environment', event.itemsSelected?.value || ''),
    [onChange]
  );

  return (
    <div
      className={styles['auth-fields']}
      data-test-id="godaddy-ans-sso-key-fields"
    >
      <div className={styles.field}>
        <label htmlFor="gd-key" className={styles.label}>
          API Key <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="gd-key"
            name="gdKey"
            value={formData.gdKey || ''}
            onChange={handleGdKeyChange}
            required
            placeholder="Enter API Key"
            testId="gd-key-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="gd-secret" className={styles.label}>
          API Secret{' '}
          <span display-if={mode === ADD_MODE} className={styles.required}>
            *
          </span>
        </label>
        <div className={styles['field-content']}>
          <TextField
            id="gd-secret"
            name="gdSecret"
            type="password"
            value={formData.gdSecret || ''}
            onChange={handleGdSecretChange}
            required={mode === ADD_MODE}
            placeholder={
              mode === EDIT_MODE
                ? 'Enter new api secret to update (leave empty to keep existing)'
                : 'Enter API Secret'
            }
            testId="gd-secret-input"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="environment" className={styles.label}>
          Environment <span className={styles.required}>*</span>
        </label>
        <div className={styles['field-content']}>
          <Select
            id="environment"
            name="environment"
            testId="environment-select"
            value={formData.environment || ''}
            placeholder="Select environment"
            options={environmentOptions}
            onChange={handleEnvironmentChange}
          />
        </div>
      </div>
    </div>
  );
};

SsoKeyForm.propTypes = {
  mode: PropTypes.oneOf([ADD_MODE, EDIT_MODE]),
  formData: PropTypes.shape({
    gdKey: PropTypes.string,
    gdSecret: PropTypes.string,
    environment: PropTypes.string
  }).isRequired,
  onChange: PropTypes.func.isRequired
};

export default SsoKeyForm;
